import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useSnackbar } from '../../hooks/useSnackbar'
import { useProductOrigins } from '../../hooks/useProductOrigins'
import { filterOptions } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import { getAsnDetails, getAsnDetailState } from './config/api'
import { AsnDetailExtended, AsnDetailStateInfoDto } from './types'
import { Filter } from '../../components/_WMS/FilterComponent/types'
import { Status } from '../../hooks/useFetchState'
import { ApiResponse } from '../../typings/ApiResponse'
import { GenericOrderBy } from '../../components/_EOS/Table/types'
import { Box, Typography, CircularProgress } from '@mui/material'

// Componente para cargar y renderizar los detalles de una fila
const AsnDetailExpandableContent: React.FC<{
  row: AsnDetailExtended
  onError: (error: any) => void
}> = ({ row, onError }) => {
  const [loading, setLoading] = useState(true)
  const [stateInfo, setStateInfo] = useState<AsnDetailStateInfoDto | null>(null)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const response = await getAsnDetailState(row.asnDetailId)
        setStateInfo(response.data)
      } catch (error) {
        onError(error)
        setStateInfo(null)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [row.asnDetailId])

  if (loading) {
    return (
      <Box
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (!stateInfo) {
    return (
      <Box style={{ padding: '16px', backgroundColor: '#f5f5f5' }}>
        <Typography variant='body2' color='textSecondary'>
          No se pudieron cargar los detalles
        </Typography>
      </Box>
    )
  }

  return (
    <Box style={{ padding: '16px', backgroundColor: '#f5f5f5' }}>
      <Box display='flex' style={{ gap: '24px' }}>
        {/* Sección: Cantidades (izquierda, columnas verticales) */}
        <Box style={{ flex: 1 }}>
          <Typography
            variant='subtitle2'
            style={{ marginBottom: '8px', color: '#555', fontWeight: 'bold' }}
          >
            Cantidades
          </Typography>
          <Box>
            <Typography variant='body2' style={{ marginBottom: '4px' }}>
              <strong>Faltantes:</strong>{' '}
              {row.missingQty == null ? 0 : row.missingQty}
            </Typography>
            <Typography variant='body2' style={{ marginBottom: '4px' }}>
              <strong>En calidad:</strong>{' '}
              {row.qualityQty == null ? 0 : row.qualityQty}
            </Typography>
            <Typography variant='body2'>
              <strong>Sobrantes:</strong>{' '}
              {row.excessQty == null ? 0 : row.excessQty}
            </Typography>
          </Box>
        </Box>

        {/* Sección: Información (derecha) */}
        <Box style={{ flex: 1 }}>
          <Typography
            variant='subtitle2'
            style={{ marginBottom: '8px', color: '#555', fontWeight: 'bold' }}
          >
            Información
          </Typography>
          <Box>
            <Typography variant='body2' style={{ marginBottom: '4px' }}>
              <strong>Estado ASN:</strong> {stateInfo.parentAsnStateName || '-'}
            </Typography>
            <Typography variant='body2' style={{ marginBottom: '4px' }}>
              <strong>Estado ASN Línea:</strong>{' '}
              {stateInfo.detailAsnStateName || '-'}
            </Typography>
            {stateInfo.lpnStateName && (
              <Typography variant='body2' style={{ marginBottom: '4px' }}>
                <strong>Estado de LPN:</strong> {stateInfo.lpnStateName}
              </Typography>
            )}
            <Typography variant='body2'>
              <strong>Descripción:</strong> {row.skuName || '-'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Comentarios (si existen) */}
      {stateInfo.comments && stateInfo.comments.length > 0 && (
        <Box
          style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #ddd'
          }}
        >
          <Typography
            variant='subtitle2'
            style={{ marginBottom: '8px', color: '#555', fontWeight: 'bold' }}
          >
            Comentarios
          </Typography>
          <Typography variant='body2'>
            {stateInfo.comments.join(', ')}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export const AsnDetailsQueryPage: React.FC = () => {
  const { callApi, state, setIdle } = useApi<ApiResponse<AsnDetailExtended>>()
  const pagination = usePagination(5)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const {
    productOriginsState: { productOrigins },
    getProductOrigins
  } = useProductOrigins()

  // Sin filtros por defecto: el usuario debe definir un filtro inicial para consultar
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  // Ordenamiento: más nuevos primero
  const [orderBy, setOrderBy] = useState<GenericOrderBy<AsnDetailExtended>>({
    field: 'asnId' as keyof AsnDetailExtended,
    direction: 'desc'
  })

  // El usuario debe definir un filtro inicial antes de poder consultar
  const [hasSearched, setHasSearched] = useState(false)

  const columnsConfig = getColumnsConfig(productOrigins)

  const requestData = (filters: Filter[], page: number, pageSize: number) => {
    setHasSearched(true)
    callApi(getAsnDetails(filters, page, pageSize, orderBy)).catch((error) => {
      openSnackBar(error as any, false)
    })
  }

  const handleSorting = (
    field: keyof AsnDetailExtended,
    direction: 'asc' | 'desc'
  ) => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleFilterChange = (value: React.SetStateAction<Filter[]>) => {
    let newFilters: Filter[]

    if (typeof value === 'function') {
      newFilters = value(selectedFilters)
    } else {
      newFilters = value
    }

    const isDeletion = newFilters.length < selectedFilters.length

    setSelectedFilters(newFilters)

    if (isDeletion) {
      pagination.setPagination(0, pagination.pageSize)
      if (newFilters.length > 0) {
        // Aún queda al menos un filtro: re-consultar con los filtros restantes
        requestData(newFilters, 0, pagination.pageSize)
      } else {
        // Se eliminó el último filtro: limpiar la grilla sin consultar
        setHasSearched(false)
        setIdle()
      }
    }
  }

  useEffect(() => {
    getProductOrigins()
  }, [])

  useEffect(() => {
    // no volver a buscar por cambio de orden hasta que el usuario haya hecho la primera consulta
    if (!hasSearched) return
    pagination.setPagination(0, pagination.pageSize)
    requestData(selectedFilters, 0, pagination.pageSize)
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={() =>
            requestData(selectedFilters, pagination.page, pagination.pageSize)
          }
          selectedFilters={selectedFilters}
          setSelectedFilters={handleFilterChange}
        />

        <GenericTableV2<AsnDetailExtended>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='asnDetailId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof AsnDetailExtended,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          styling={{
            dense: false,
            hoverEffect: true
          }}
          expandable={{
            getDetailData: (row: AsnDetailExtended) => row,
            renderContent: (_, row: AsnDetailExtended) => (
              <AsnDetailExpandableContent
                row={row}
                onError={(error) => openSnackBar(error as any, false)}
              />
            ),
            headerText: 'Detalles',
            expansionTriggerMode: 'row-click',
            expandAllByDefault: false
          }}
        />
      </StandardPage>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
